// ?use_ios_ui — twin of iOS 0.7.0 ManualAdjustmentPanel (wheel-sizing).
//
// Portrait: a near-black card. Top row (42px): the wheel's size tile, the
// MASK tile, trash. A hairline. Then camber and rotate stacked, the D-pad,
// and the DEPTH slider — the row exactly as tall as the pad.
// Landscape: a right-edge column — size + trash, pad + depth, camber +
// rotate side by side. No MASK tile: a narrow column leaves a slider no
// track; the answer is kept and the tile returns upright.
//
// Steps are Android's MANUAL_ADJUSTMENT_STEPS (5× iOS, an intentional,
// documented divergence) passed in by the caller.

import { ChevronDown, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { IosDragStrip } from '@/components/ios-ui/ios-drag-strip/ios-drag-strip'
import { DepthSlider } from '@/components/ios-ui/depth-slider/depth-slider'
import { PanelSlider } from '@/components/ios-ui/panel-slider/panel-slider'
import { IosJoystickPad, joystickPadSide } from '@/components/ios-ui/ios-joystick-pad/ios-joystick-pad'
import { IOS_UI_MONO, IOS_UI_PALETTE, PANEL_LABEL_STYLE, panelTileStyle } from '@/components/ios-ui/ios-ui-palette/ios-ui-palette'
import { CURRENT_WHEEL_SIZE_RANGE } from '@/models/current-wheel-size'
import { sizeDisplayText, type TWheelSizeOptions } from '@/models/wheel-size-options'

/** Radians per pixel on the camber and rotate strips (iOS 0.005). */
const STRIP_SENSITIVITY = 0.005
/** Rotation has no limit; its gauge pins at 45°. */
const ROTATE_GAUGE_RANGE_DEG = 45
const RAD_TO_DEG = 180 / Math.PI

export interface IosAdjustmentPanelProps {
  isLandscape: boolean
  sizeOptions: TWheelSizeOptions
  /** The size a choice is shown at; the other kinds carry their own. */
  shownSizeInches: number | null
  onSizeChange: (inches: number) => void
  /** The MASK answer, or null while unanswered. */
  currentWheelSizeInches: number | null
  /** The selected wheel's size the MASK parks at; null hides the tile. */
  ringSizeInches: number | null
  onCurrentWheelSizeChange: (inches: number) => void
  onDelete: () => void
  onHeightChange: (delta: number) => void
  onHorizontalChange: (delta: number) => void
  onDepthChange: (delta: number) => void
  onTiltChange: (delta: number) => void
  onYawChange: (delta: number) => void
  tiltAngleRad: number
  yawAngleRad: number
  maxCamberRad: number
  heightStep: number
  horizontalStep: number
  /** One arrow press, when the caller maps directions itself (the overlay
   *  rotates them to the user's view). Absent: the unrotated mapping. */
  onPad?: (direction: 'up' | 'down' | 'left' | 'right') => void
}

export function IosAdjustmentPanel(props: IosAdjustmentPanelProps) {
  return props.isLandscape ? <LandscapePanel {...props} /> : <PortraitPanel {...props} />
}

function PortraitPanel(props: IosAdjustmentPanelProps) {
  const buttonSize = 44
  return (
    <div data-testid="ios-adjustment-panel" style={{ ...cardStyle(), padding: 16, margin: '0 8px 4px' }}>
      <div style={{ display: 'flex', gap: 8, height: 42 }}>
        <SizeTile {...props} />
        <MaskTile {...props} />
        <DeleteTile onDelete={props.onDelete} side={42} />
      </div>
      <div style={{ height: 1, background: IOS_UI_PALETTE.tileStroke, margin: '16px 0' }} />
      <div style={{ display: 'flex', gap: 16, height: joystickPadSide(buttonSize) }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <CamberStrip {...props} compact={false} />
          <RotateStrip {...props} compact={false} />
        </div>
        <Pad {...props} buttonSize={buttonSize} />
        <div style={{ flex: 1, display: 'flex' }}>
          <DepthSlider onDrag={props.onDepthChange} />
        </div>
      </div>
    </div>
  )
}

function LandscapePanel(props: IosAdjustmentPanelProps) {
  const buttonSize = 40
  const padSide = joystickPadSide(buttonSize)
  const depthWidth = 56
  const gap = 10
  const padding = 12
  return (
    <div
      data-testid="ios-adjustment-panel"
      // border-box: the width carries the padding and the 1px border too.
      style={{ ...cardStyle(), padding, width: padSide + gap + depthWidth + 2 * padding + 2, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div style={{ display: 'flex', gap: 8, height: 36 }}>
        <SizeTile {...props} />
        <div style={{ flex: 1 }} />
        <DeleteTile onDelete={props.onDelete} side={36} />
      </div>
      <div style={{ display: 'flex', gap, height: padSide }}>
        <Pad {...props} buttonSize={buttonSize} />
        <div style={{ width: depthWidth, display: 'flex' }}>
          <DepthSlider onDrag={props.onDepthChange} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, height: 70 }}>
        <CamberStrip {...props} compact />
        <RotateStrip {...props} compact />
      </div>
    </div>
  )
}

// ── Tiles ───────────────────────────────────────────────────────────────

/** The wheel's own size, top-left, mirroring delete top-right. No label:
 *  the inch mark says what the number is. */
function SizeTile({ sizeOptions, shownSizeInches, onSizeChange }: IosAdjustmentPanelProps) {
  const [open, setOpen] = useState(false)
  if (sizeOptions.kind === 'hidden') return null
  const isChoice = sizeOptions.kind === 'choice'
  const shown = isChoice ? (shownSizeInches ?? sizeOptions.reference) : sizeOptions.inches
  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <button
        type="button"
        data-testid="ios-size-tile"
        aria-label={`Wheel size, ${sizeDisplayText(shown)} inches`}
        aria-haspopup={isChoice ? 'menu' : undefined}
        aria-expanded={isChoice ? open : undefined}
        onClick={() => isChoice && setOpen((o) => !o)}
        style={{
          ...panelTileStyle(),
          height: '100%',
          padding: '0 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          cursor: isChoice ? 'pointer' : 'default',
        }}
      >
        <span data-testid="ios-size-value" style={{ fontFamily: IOS_UI_MONO, fontSize: 21, fontWeight: 700, color: IOS_UI_PALETTE.accentOnDark }}>
          {`${sizeDisplayText(shown)}"`}
        </span>
        {isChoice && (
          <span data-testid="ios-size-chevron" style={{ display: 'inline-flex', color: IOS_UI_PALETTE.label }}>
            <ChevronDown size={14} strokeWidth={2.5} />
          </span>
        )}
      </button>
      {isChoice && open && (
        <SizeMenu
          sizes={sizeOptions.sizes}
          shown={shown}
          onPick={(inches) => {
            setOpen(false)
            onSizeChange(inches)
          }}
        />
      )}
    </div>
  )
}

/** iOS's Menu + Picker: smallest first, a check on the shown size. Opens
 *  upward, because the panel sits at the bottom of the screen. */
function SizeMenu({ sizes, shown, onPick }: { sizes: readonly number[]; shown: number; onPick: (inches: number) => void }) {
  return (
    <div
      data-testid="ios-size-menu"
      role="menu"
      style={{
        position: 'absolute',
        left: 0,
        bottom: 'calc(100% + 8px)',
        minWidth: 120,
        padding: 6,
        borderRadius: 14,
        background: 'rgba(44, 44, 46, 0.98)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
        zIndex: 10,
      }}
    >
      {sizes.map((inches) => (
        <button
          key={inches}
          type="button"
          role="menuitemradio"
          aria-checked={inches === shown}
          data-testid={`ios-size-option-${inches}`}
          data-inches={String(inches)}
          onClick={() => onPick(inches)}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            padding: '10px 12px',
            border: 'none',
            borderRadius: 8,
            background: 'transparent',
            color: 'white',
            fontFamily: IOS_UI_MONO,
            fontSize: 16,
            cursor: 'pointer',
          }}
        >
          <span>{`${sizeDisplayText(inches)}"`}</span>
          <span style={{ color: IOS_UI_PALETTE.accentOnDark }}>{inches === shown ? '✓' : ''}</span>
        </button>
      ))}
    </div>
  )
}

/** The size of the wheels ALREADY on the car; sets every wheel's mask ring.
 *  Parks at the selected wheel's size until answered. */
function MaskTile({ isLandscape, currentWheelSizeInches, ringSizeInches, onCurrentWheelSizeChange }: IosAdjustmentPanelProps) {
  if (isLandscape) return null
  if (ringSizeInches == null) return <div style={{ flex: 1 }} />
  const answer = currentWheelSizeInches ?? clampToRange(ringSizeInches)
  return (
    <div
      data-testid="ios-mask-tile"
      style={{ ...panelTileStyle(), flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', minWidth: 0 }}
    >
      <span style={PANEL_LABEL_STYLE}>MASK</span>
      <PanelSlider
        testId="ios-mask-slider"
        ariaLabel="Current wheel size"
        value={answer}
        min={CURRENT_WHEEL_SIZE_RANGE.min}
        max={CURRENT_WHEEL_SIZE_RANGE.max}
        step={1}
        onChange={onCurrentWheelSizeChange}
      />
      <span
        data-testid="ios-mask-value"
        style={{ fontFamily: IOS_UI_MONO, fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.85)', width: 30, textAlign: 'right' }}
      >
        {`${Math.round(answer)}"`}
      </span>
    </div>
  )
}

function DeleteTile({ onDelete, side }: { onDelete: () => void; side: number }) {
  return (
    <button
      type="button"
      data-testid="ios-delete"
      aria-label="Delete wheel"
      onClick={onDelete}
      style={{
        ...panelTileStyle(),
        width: side,
        height: side,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgba(255,255,255,0.85)',
        cursor: 'pointer',
      }}
    >
      <Trash2 size={16} strokeWidth={2} />
    </button>
  )
}

function CamberStrip({ onTiltChange, tiltAngleRad, maxCamberRad, compact }: IosAdjustmentPanelProps & { compact: boolean }) {
  // Right does what up does: the top of the wheel leans toward the camera.
  return (
    <IosDragStrip
      testId="ios-camber"
      label="Camber"
      sensitivity={STRIP_SENSITIVITY}
      onDrag={onTiltChange}
      gaugeRangeDeg={maxCamberRad * RAD_TO_DEG}
      currentAngleDeg={tiltAngleRad * RAD_TO_DEG}
      compact={compact}
    />
  )
}

function RotateStrip({ onYawChange, yawAngleRad, compact }: IosAdjustmentPanelProps & { compact: boolean }) {
  return (
    <IosDragStrip
      testId="ios-rotate"
      label="Rotate"
      sensitivity={STRIP_SENSITIVITY}
      onDrag={onYawChange}
      gaugeRangeDeg={ROTATE_GAUGE_RANGE_DEG}
      currentAngleDeg={yawAngleRad * RAD_TO_DEG}
      compact={compact}
    />
  )
}

function Pad({ onHeightChange, onHorizontalChange, heightStep, horizontalStep, onPad, buttonSize }: IosAdjustmentPanelProps & { buttonSize: number }) {
  if (onPad != null) {
    return (
      <IosJoystickPad
        buttonSize={buttonSize}
        onUp={() => onPad('up')}
        onDown={() => onPad('down')}
        onLeft={() => onPad('left')}
        onRight={() => onPad('right')}
      />
    )
  }
  return (
    <IosJoystickPad
      buttonSize={buttonSize}
      onUp={() => onHeightChange(heightStep)}
      onDown={() => onHeightChange(-heightStep)}
      onLeft={() => onHorizontalChange(horizontalStep)}
      onRight={() => onHorizontalChange(-horizontalStep)}
    />
  )
}

function cardStyle() {
  return {
    boxSizing: 'border-box' as const,
    background: IOS_UI_PALETTE.panelBackground,
    border: `1px solid ${IOS_UI_PALETTE.tileStroke}`,
    borderRadius: 18,
    pointerEvents: 'auto' as const,
  }
}

function clampToRange(inches: number): number {
  return Math.min(Math.max(inches, CURRENT_WHEEL_SIZE_RANGE.min), CURRENT_WHEEL_SIZE_RANGE.max)
}
